import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import './ApiKeys.css';

const AVAILABLE_SCOPES = [
  {
    id: 'tokenize:write',
    label: 'tokenize:write',
    title: 'Tokenize (Write)',
    desc: 'Encrypt sensitive PII and issue format-preserving tokens',
    color: '#059669',
    bg: '#ecfdf5',
    border: '#a7f3d0'
  },
  {
    id: 'detokenize:read',
    label: 'detokenize:read',
    title: 'Detokenize (Read)',
    desc: 'Reconstitute encrypted tokens back to cleartext values',
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe'
  },
  {
    id: 'gateway:llm',
    label: 'gateway:llm',
    title: 'LLM Gateway',
    desc: 'Access Zero-Trust AI gateway & real-time chat completions',
    color: '#7c3aed',
    bg: '#f5f3ff',
    border: '#ddd6fe'
  },
  {
    id: 'schemas:read',
    label: 'schemas:read',
    title: 'Schemas (Read)',
    desc: 'Inspect cryptographic schema definitions and rules',
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a'
  },
  {
    id: 'schemas:write',
    label: 'schemas:write',
    title: 'Schemas (Write)',
    desc: 'Create and update custom enterprise PII schema policies',
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fecaca'
  },
  {
    id: 'audit:read',
    label: 'audit:read',
    title: 'Audit Logs (Read)',
    desc: 'Export and query compliance access logs and tamper trails',
    color: '#0891b2',
    bg: '#ecfeff',
    border: '#a5f3fc'
  }
];

const PRESETS = [
  {
    name: '⚡ Full Enclave Access',
    scopes: AVAILABLE_SCOPES.map(s => s.id),
    desc: 'All 6 scopes'
  },
  {
    name: '🤖 LLM Gateway Only',
    scopes: ['gateway:llm'],
    desc: 'Restricted to LLM completions'
  },
  {
    name: '🔒 Ingestion Only',
    scopes: ['tokenize:write'],
    desc: 'Only tokenization & encryption'
  },
  {
    name: '📊 Compliance Auditor',
    scopes: ['schemas:read', 'audit:read'],
    desc: 'Read schemas and audit logs'
  }
];

const ApiKeys = () => {
  const { tenant, token } = useAuth();

  const [keys, setKeys] = useState([]);
  const [showSecret, setShowSecret] = useState('');
  const [newKeyDetails, setNewKeyDetails] = useState(null);

  // Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [ttlDays, setTtlDays] = useState('30');
  const [selectedScopes, setSelectedScopes] = useState(AVAILABLE_SCOPES.map(s => s.id));
  const [ipWhitelist, setIpWhitelist] = useState('');

  const [pageState, setPageState] = useState('loading');
  const [loadingAction, setLoadingAction] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copiedKeyId, setCopiedKeyId] = useState(null);

  const isFreePlan = tenant?.plan === 'Free Plan';

  const fetchKeys = async () => {
    if (!tenant?.id || !token) return;

    setPageState('loading');
    setErrorMessage('');

    try {
      const res = await fetch(
        `${API_BASE}/admin/api-key/${tenant.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 403) {
        setPageState('forbidden');
        return;
      }

      if (!res.ok) {
        throw new Error('Failed to load API keys');
      }

      const data = await res.json();
      setKeys(data);
      setPageState('ready');
    } catch (err) {
      console.error(err);
      setErrorMessage('Unable to load API keys.');
      setPageState('error');
    }
  };

  useEffect(() => {
    fetchKeys();
  }, [tenant?.id, token]);

  const toggleScope = (scopeId) => {
    if (selectedScopes.includes(scopeId)) {
      if (selectedScopes.length === 1) {
        alert('An API key must have at least one scope.');
        return;
      }
      setSelectedScopes(selectedScopes.filter(s => s !== scopeId));
    } else {
      setSelectedScopes([...selectedScopes, scopeId]);
    }
  };

  const applyPreset = (presetScopes) => {
    setSelectedScopes(presetScopes);
  };

  const handleCreateKey = async (e) => {
    if (e) e.preventDefault();

    if (isFreePlan && keys.some(k => k.enabled)) {
      alert('Free plan allows only one active API key.');
      return;
    }

    const parsedTTL = ttlDays ? parseInt(ttlDays, 10) : null;
    if (isFreePlan && (!parsedTTL || parsedTTL > 7)) {
      alert('Free plan requires a max TTL of 7 days.');
      return;
    }

    if (selectedScopes.length === 0) {
      alert('Please select at least one permission scope.');
      return;
    }

    setLoadingAction(true);
    try {
      const payload = {
        name: keyName.trim() || undefined,
        ttlDays: parsedTTL || undefined,
        scopes: selectedScopes,
        ipWhitelist: ipWhitelist.trim() || undefined
      };

      const res = await fetch(`${API_BASE}/admin/api-key/${tenant.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.status === 403) {
        setPageState('forbidden');
        return;
      }

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Key generation failed');
      }

      const data = await res.json();
      const rawSecret = data.apiKey || data.key;
      setShowSecret(rawSecret);
      setNewKeyDetails({
        name: data.name || keyName || 'Standard Key',
        scopes: data.scopes || selectedScopes.join(','),
        ipWhitelist: data.ipWhitelist || ipWhitelist || 'All IPs (*)'
      });

      setIsModalOpen(false);
      setKeyName('');
      setTtlDays(isFreePlan ? '7' : '30');
      setIpWhitelist('');
      setSelectedScopes(AVAILABLE_SCOPES.map(s => s.id));
      fetchKeys();
    } catch (err) {
      alert(`❌ Failed to generate API key: ${err.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const disableKey = async (id) => {
    if (!window.confirm('Revoke and permanently disable this API key? Systems utilizing this key will lose immediate access.')) return;

    setLoadingAction(true);
    try {
      const res = await fetch(`${API_BASE}/admin/api-key/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 403) {
        setPageState('forbidden');
        return;
      }

      if (!res.ok) {
        throw new Error('Failed to disable key');
      }

      fetchKeys();
    } catch {
      alert('❌ Failed to disable key');
    } finally {
      setLoadingAction(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKeyId(id);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  /* ---------------- RENDER GATES ---------------- */

  if (pageState === 'loading') {
    return (
      <div className="container apikeys-page">
        <div className="page-card">Loading API key configuration…</div>
      </div>
    );
  }

  if (pageState === 'forbidden') {
    return (
      <div className="container apikeys-page">
        <div className="page-card alert error">
          You do not have permission to manage API keys.
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="container apikeys-page">
        <div className="page-card alert error">{errorMessage}</div>
      </div>
    );
  }

  /* ---------------- MAIN PAGE ---------------- */

  return (
    <div className="container apikeys-page">
      <header className="page-header">
        <div className="header-badge">🔐 Zero-Trust Credential Security</div>
        <h2>Enterprise API Key Management</h2>
        <p className="page-subtitle">
          Manage least-privilege RBAC permission scopes, CIDR IP whitelisting, and key rotation lifecycles.
        </p>
      </header>

      {/* Top action bar */}
      <div className="apikeys-action-bar">
        <div className="plan-status-pill">
          <span className="dot active"></span>
          Plan: <strong>{tenant?.plan || 'Standard'}</strong> ({keys.filter(k => k.enabled).length} Active Key{keys.filter(k => k.enabled).length === 1 ? '' : 's'})
        </div>

        <button
          className="btn btn-primary btn-icon"
          onClick={() => {
            if (isFreePlan && keys.some(k => k.enabled)) {
              alert('Free plan allows only 1 active API key. Please upgrade or revoke your existing key.');
              return;
            }
            setIsModalOpen(true);
          }}
          disabled={loadingAction || (isFreePlan && keys.some(k => k.enabled))}
        >
          <span className="btn-icon-symbol">+</span> Create Enclave API Key
        </button>
      </div>

      {isFreePlan && keys.some(k => k.enabled) && (
        <div className="alert warning" style={{ marginBottom: 20 }}>
          ⚡ <strong>Free Plan Notice:</strong> Your plan includes 1 active API key with a maximum 7-day TTL. Upgrade to Enterprise to unlock unlimited multi-tenant keys, 365-day lifetimes, and advanced CIDR IP whitelisting.
        </div>
      )}

      {/* New Key Revealed Modal */}
      {showSecret && (
        <section className="page-card secret-card animate-fade-in">
          <div className="secret-header">
            <div className="secret-icon">🔑</div>
            <div>
              <h3>New API Key Generated Successfully</h3>
              <p className="secret-warning">
                This secret key will <strong>never be shown again</strong>. Please copy and store it securely in your secret manager or environment configuration.
              </p>
            </div>
          </div>

          <div className="secret-meta-grid">
            <div className="secret-meta-item">
              <span className="meta-label">Key Name</span>
              <span className="meta-value">{newKeyDetails?.name}</span>
            </div>
            <div className="secret-meta-item">
              <span className="meta-label">IP Whitelist</span>
              <span className="meta-value">{newKeyDetails?.ipWhitelist || '🌐 All IPs (*)'}</span>
            </div>
            <div className="secret-meta-item" style={{ gridColumn: 'span 2' }}>
              <span className="meta-label">Configured Scopes</span>
              <div className="scope-pills-wrap">
                {newKeyDetails?.scopes?.split(',').map((sc, i) => (
                  <span key={i} className="scope-pill active">{sc.trim()}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="secret-display-box">
            <pre className="secret-code">{showSecret}</pre>
            <button
              className="btn btn-primary btn-copy"
              onClick={() => {
                navigator.clipboard.writeText(showSecret);
                alert('✅ API Key copied to clipboard!');
              }}
            >
              📋 Copy Secret Key
            </button>
          </div>

          <div className="form-actions" style={{ marginTop: 16 }}>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowSecret('');
                setNewKeyDetails(null);
              }}
            >
              I have safely stored this key
            </button>
          </div>
        </section>
      )}

      {/* Creation Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-api-key animate-slide-up">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Create Enclave API Key</h3>
                <p className="modal-subtitle">Configure least-privilege RBAC scopes and network IP boundaries.</p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
                disabled={loadingAction}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateKey}>
              {/* Key Name & TTL */}
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">
                    Key Friendly Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. LLM Inference Proxy, Payment Ingestion Worker"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    required
                  />
                  <span className="input-hint">Identifies this credential in audit logs</span>
                </div>

                <div className="form-group">
                  <label className="form-label">Key Expiration (TTL)</label>
                  <select
                    className="form-input"
                    value={ttlDays}
                    onChange={(e) => setTtlDays(e.target.value)}
                  >
                    <option value="1">1 Day (Short-lived session)</option>
                    <option value="7">7 Days</option>
                    {!isFreePlan && (
                      <>
                        <option value="30">30 Days (Recommended)</option>
                        <option value="90">90 Days</option>
                        <option value="180">180 Days</option>
                        <option value="365">365 Days (1 Year)</option>
                        <option value="">Never Expires (Enterprise)</option>
                      </>
                    )}
                  </select>
                  <span className="input-hint">
                    {isFreePlan ? 'Free plan max TTL is 7 days.' : 'Automated expiry reduces credential compromise impact.'}
                  </span>
                </div>
              </div>

              {/* Scope Presets */}
              <div className="form-group" style={{ marginTop: 12 }}>
                <div className="label-with-badge">
                  <label className="form-label">RBAC Access Presets</label>
                  <span className="sample-badge">1-Click Setup</span>
                </div>
                <div className="preset-buttons-row">
                  {PRESETS.map((p, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="preset-btn"
                      onClick={() => applyPreset(p.scopes)}
                    >
                      <span className="preset-name">{p.name}</span>
                      <span className="preset-sub">{p.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Granular Scopes Selection */}
              <div className="form-group">
                <label className="form-label">
                  Granular Permissions &amp; Scopes <span className="req">*</span>
                </label>
                <div className="scopes-grid">
                  {AVAILABLE_SCOPES.map((scope) => {
                    const isChecked = selectedScopes.includes(scope.id);
                    return (
                      <div
                        key={scope.id}
                        className={`scope-card ${isChecked ? 'selected' : ''}`}
                        onClick={() => toggleScope(scope.id)}
                      >
                        <div className="scope-header">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="scope-checkbox"
                          />
                          <span
                            className="scope-tag"
                            style={{
                              backgroundColor: scope.bg,
                              color: scope.color,
                              borderColor: scope.border
                            }}
                          >
                            {scope.label}
                          </span>
                        </div>
                        <div className="scope-title">{scope.title}</div>
                        <div className="scope-desc">{scope.desc}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CIDR IP Whitelist */}
              <div className="form-group" style={{ marginTop: 16 }}>
                <div className="label-with-badge">
                  <label className="form-label">Network IP / CIDR Whitelist</label>
                  <span className="sample-badge">Optional Zero-Trust</span>
                </div>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. 192.168.1.50, 10.0.0.0/16, 127.0.0.1 (or leave blank for all)"
                  value={ipWhitelist}
                  onChange={(e) => setIpWhitelist(e.target.value)}
                />
                <span className="input-hint">
                  Restrict requests using this key to specific IPv4 addresses or CIDR subnets. Requests originating from outside these IPs will receive HTTP 403 Forbidden.
                </span>
              </div>

              {/* Modal Actions */}
              <div className="modal-actions" style={{ marginTop: 24 }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loadingAction}
                >
                  {loadingAction ? 'Generating Enclave Key…' : 'Generate API Key'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loadingAction}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Keys Table */}
      <section className="page-card table-card">
        <div className="table-header-row">
          <h3>Configured API Credentials</h3>
          <span className="table-count">{keys.length} Credential{keys.length === 1 ? '' : 's'} Total</span>
        </div>

        {keys.length === 0 ? (
          <div className="empty-keys-state">
            <div className="empty-icon">🔑</div>
            <h4>No API Keys Configured</h4>
            <p>Generate an API key with granular scopes to authenticate your application, LLM gateway, or microservice.</p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={() => setIsModalOpen(true)}
            >
              Generate First API Key
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Credential Name &amp; ID</th>
                  <th>RBAC Scopes</th>
                  <th>IP Whitelist</th>
                  <th>Expiration</th>
                  <th>Status</th>
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const keyScopes = k.scopeList || (k.scopes ? k.scopes.split(',') : []);
                  const isRevoked = !k.enabled;
                  const formattedExp = k.expiresAt
                    ? new Date(k.expiresAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : 'Never (Persistent)';

                  return (
                    <tr key={k.id} className={isRevoked ? 'row-revoked' : ''}>
                      <td>
                        <div className="key-name-block">
                          <strong>{k.name || 'Enclave API Key'}</strong>
                          <span className="key-preview-code">
                            {k.maskedKey || k.apiKeyPreview || '••••••••'}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="scopes-badge-list">
                          {keyScopes.length === 6 ? (
                            <span className="scope-pill active-all">⚡ Full Access (All 6 Scopes)</span>
                          ) : keyScopes.length === 0 ? (
                            <span className="scope-pill revoked">No Scopes</span>
                          ) : (
                            keyScopes.map((sc, idx) => (
                              <span key={idx} className="scope-pill active">
                                {sc.trim()}
                              </span>
                            ))
                          )}
                        </div>
                      </td>

                      <td>
                        {k.ipWhitelist ? (
                          <span className="ip-badge restricted" title={k.ipWhitelist}>
                            🛡️ {k.ipWhitelist.length > 22 ? k.ipWhitelist.substring(0, 20) + '…' : k.ipWhitelist}
                          </span>
                        ) : (
                          <span className="ip-badge unrestricted">
                            🌐 All IPs (*)
                          </span>
                        )}
                      </td>

                      <td>
                        <span className="exp-date">{formattedExp}</span>
                      </td>

                      <td>
                        <span className={`badge ${k.enabled ? 'active' : 'revoked'}`}>
                          {k.enabled ? '● Active' : '✕ Revoked'}
                        </span>
                      </td>

                      <td className="key-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={!k.enabled || loadingAction}
                          onClick={() => disableKey(k.id)}
                          title="Revoke and disable this key"
                        >
                          Revoke
                        </button>

                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => copyToClipboard(k.apiKeyPreview || k.id, k.id)}
                          title="Copy Key Identifier"
                        >
                          {copiedKeyId === k.id ? '✓ Copied' : 'Copy ID'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ApiKeys;
